"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useUnreadSnapshot = useUnreadSnapshot;
const react_1 = require("react");
/**
 * Captures a one-time snapshot of the unread boundary when a chat is opened.
 * Shared by the web and mobile chat panels.
 *
 * Opening a chat marks its messages read, so the live unread count drops to
 * zero and can't drive the "New messages" divider. We snapshot the pre-open
 * boundary once, hold it stable while the chat stays open, and recompute only
 * when a different chat is opened.
 *
 * The count and boundary come from the chat-list cache (unreadMessageCount /
 * lastMessageRead), NOT the message-thread payload: the cache is kept current
 * by socket events, so it stays correct whether the unread messages were
 * fetched fresh or arrived over the socket into an already-open session. The
 * message-thread fields are only refreshed on a full fetch and go stale in the
 * socket case. Returns null when there is nothing unread to mark.
 *
 * A never-opened chat has unread messages but no `lastMessageRead` (the read
 * pointer is only established once the user reads). In that case we still
 * capture a snapshot, with an empty `boundaryMessageId` — the consumer treats
 * that as "everything is unread, boundary is before the first real message".
 *
 * The capture happens synchronously during render (via refs, not an effect) on
 * purpose: it makes the snapshot available on the very first render where the
 * chat's messages are ready, so the consumer can scroll to the divider in a
 * layout effect BEFORE the first paint. If we captured in an effect (post
 * paint) the list would paint at the bottom first and then visibly jump to the
 * divider on the next frame.
 */
function useUnreadSnapshot(chatId, isReady, unreadCount, lastMessageRead) {
    const capturedChatIdRef = (0, react_1.useRef)(null);
    const snapshotRef = (0, react_1.useRef)(null);
    if (capturedChatIdRef.current !== chatId) {
        if (isReady) {
            // First render for this chat with its messages loaded: capture once.
            capturedChatIdRef.current = chatId;
            // Empty boundaryMessageId (never-opened chat with no read pointer) is
            // intentional — the consumer positions the divider before the first real
            // message in that case.
            snapshotRef.current =
                unreadCount > 0
                    ? { boundaryMessageId: lastMessageRead !== null && lastMessageRead !== void 0 ? lastMessageRead : "", unreadCount }
                    : null;
        }
        else {
            // Chat changed (or first open) but its messages haven't loaded yet —
            // drop any previous chat's snapshot until this one is ready.
            snapshotRef.current = null;
        }
    }
    return capturedChatIdRef.current === chatId ? snapshotRef.current : null;
}
